import {
  Box,
  Button,
  CircularProgress,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { format } from "date-fns";
import { useMemo, useState } from "react";
import { useQueryClient } from "react-query";
import { MdCheck } from "react-icons/md";
import { toast } from "react-toastify";

import { useBudget } from "../../../contexts/BudgetContext/projectbudgets.context";
import { queryKeys } from "../../../queries/keys";
import {
  RegistrationStep,
  useProject,
} from "../../../contexts/project.context";
import { TabActive } from "../../../types";
import { currencyFormatter } from "../../../utils/currencyFormatter";
import { normalizeProjectType } from "../../../utils/opcoesBriefingInicial";
import {
  BUDGET_TYPE_LABEL,
  buildFinancialStageRows,
  buildPendencies,
  countPendencies,
  formatEventLocation,
  formatEventPeriod,
  getAuditResults,
  getBudgetName,
  getBudgetNumber,
  getBudgetRowValue,
  getBudgetStatusDisplay,
  type Pendency,
} from "../utils/overview";
import {
  CardLink,
  Field,
  FieldGrid,
  OVERVIEW_COLORS,
  OverviewCard,
  StatusPill,
} from "./OverviewPrimitives";

export interface ProjectOverviewProps {
  /** Troca de aba pelo fluxo da página (autosave, modal de alterações). */
  onNavigateTab?: (tab: TabActive) => void;
}

const formatMoney = (value: number) => currencyFormatter.format(value || 0);

// Cada linha é um grid próprio: colunas de valor com largura fixa para os
// números alinharem entre as etapas (com `auto` cada linha media a sua).
const FINANCIAL_GRID_COLUMNS = "minmax(0, 1fr) 108px 108px";

const formatPercent = (value: number) =>
  `${value.toFixed(2).replace(".", ",")}%`;

function EventCard({ onEdit }: { onEdit: () => void }) {
  const { event } = useProject();
  const people = event?.numberOfPeople;

  return (
    <OverviewCard
      title="Evento"
      testId="overview-event"
      action={<CardLink onClick={onEdit}>Editar</CardLink>}
    >
      <FieldGrid>
        <Field
          label="Data"
          value={formatEventPeriod(event?.startDate, event?.endDate, true)}
        />
        <Field
          label="Participantes"
          value={
            people ? `${people} ${people === 1 ? "pessoa" : "pessoas"}` : ""
          }
        />
        <Field label="Tipo" value={normalizeProjectType(event?.type)} />
        <Field label="Local" value={formatEventLocation(event)} />
      </FieldGrid>
    </OverviewCard>
  );
}

function ClientCard({ onEdit }: { onEdit: () => void }) {
  const { client, project } = useProject();
  const clientName =
    client?.clientSelected?.nickname || client?.clientSelected?.name;
  const requesting = client?.requestings?.[0];
  const requesterName = requesting?.name || project?.requester?.name;
  const email = requesting?.email || project?.requester?.email;
  const phone = project?.requester?.phone;

  return (
    <OverviewCard
      title="Cliente"
      testId="overview-client"
      action={<CardLink onClick={onEdit}>Dados cadastrais</CardLink>}
    >
      <FieldGrid>
        <Field label="Cliente" value={clientName} />
        <Field label="Solicitante" value={requesterName} />
        <Field label="Telefone" value={phone} />
        <Field
          label="Email"
          value={
            email ? (
              <Link href={`mailto:${email}`} underline="hover">
                {email}
              </Link>
            ) : (
              ""
            )
          }
        />
      </FieldGrid>
    </OverviewCard>
  );
}

function TeamCard({ onEdit }: { onEdit: () => void }) {
  const { assignTime } = useProject();
  const groups = (assignTime?.groups ?? []).filter((g) => g?.group);

  const groupTitle = {
    m: 0,
    mb: 1.25,
    fontSize: 13,
    fontWeight: 500,
    color: OVERVIEW_COLORS.ink2,
  };

  return (
    <OverviewCard
      title="Time"
      testId="overview-team"
      action={<CardLink onClick={onEdit}>Editar time</CardLink>}
    >
      <Box
        sx={{
          display: "grid",
          gap: 3,
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            lg: "minmax(0, 3fr) minmax(0, 4fr)",
          },
        }}
      >
        <Box>
          <Typography component="h3" sx={groupTitle}>
            Atendimento
          </Typography>
          <FieldGrid columns={3}>
            <Field
              label="Novos negócios - Comercial"
              value={assignTime?.commercial?.name}
            />
            <Field
              label="Atendimento 1"
              value={assignTime?.firstAttendance?.name}
            />
            <Field
              label="Atendimento 2"
              value={assignTime?.secondAttendance?.name}
            />
          </FieldGrid>
        </Box>

        <Box
          display="flex"
          flexDirection="column"
          gap={2}
          sx={{
            pl: { lg: 3 },
            borderLeft: { lg: `1px solid ${OVERVIEW_COLORS.line2}` },
          }}
        >
          {groups.length === 0 && (
            <Box>
              <Typography component="h3" sx={groupTitle}>
                Células
              </Typography>
              <Field label="Célula" value="" />
            </Box>
          )}
          {groups.map((projectGroup, index) => (
            <Box key={projectGroup._id ?? `${projectGroup.group?._id}-${index}`}>
              <Typography component="h3" sx={groupTitle}>
                {`Célula ${index + 1}${
                  projectGroup.group?.name
                    ? `, grupo ${projectGroup.group.name}`
                    : ""
                }`}
              </Typography>
              <FieldGrid columns={3}>
                <Field label="Líder" value={projectGroup.leader?.name} />
                <Field
                  label="1º Coordenador/Produtor"
                  value={projectGroup.firstCoordinator?.name}
                />
                <Field
                  label="2º Coordenador/Produtor"
                  value={projectGroup.secondCoordinator?.name}
                />
              </FieldGrid>
            </Box>
          ))}
        </Box>
      </Box>
    </OverviewCard>
  );
}

function PendencyRow({
  pendency,
  action,
}: {
  pendency: Pendency;
  action: React.ReactNode;
}) {
  const critical = pendency.severity === "critical";
  return (
    <Box
      data-testid={`pendency-${pendency.key}`}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        py: 2,
        borderTop: `1px solid ${OVERVIEW_COLORS.line2}`,
        flexWrap: { xs: "wrap", sm: "nowrap" },
      }}
    >
      <Box
        sx={{
          width: 4,
          alignSelf: "stretch",
          borderRadius: "2px",
          bgcolor: critical ? OVERVIEW_COLORS.red : OVERVIEW_COLORS.orange,
        }}
      />
      <Box flex={1} display="flex" flexDirection="column" gap={0.5} minWidth={0}>
        <Box display="flex" alignItems="center" gap={1.25} flexWrap="wrap">
          <Typography sx={{ fontSize: 15, fontWeight: 500 }}>
            {pendency.title}
          </Typography>
          <StatusPill tone={critical ? "red" : "orange"}>
            {critical ? "Crítica" : "Atenção"}
          </StatusPill>
        </Box>
        <Typography sx={{ fontSize: 14, color: OVERVIEW_COLORS.muted }}>
          {pendency.description}
        </Typography>
      </Box>
      {action}
    </Box>
  );
}

function PendenciesCard({
  pendencies,
  onGoToBudget,
  onConfirmEventData,
  confirming,
}: {
  pendencies: Pendency[];
  onGoToBudget: () => void;
  onConfirmEventData: () => void;
  confirming: boolean;
}) {
  const { critical, attention } = countPendencies(pendencies);

  const buttonSx = { textTransform: "none", whiteSpace: "nowrap", flexShrink: 0 };

  return (
    <OverviewCard
      title="Pendências do projeto"
      testId="overview-pendencies"
      extra={
        <>
          {critical > 0 && (
            <StatusPill tone="red">
              {`${critical} ${critical === 1 ? "crítica" : "críticas"}`}
            </StatusPill>
          )}
          {attention > 0 && (
            <StatusPill tone="orange">{`${attention} atenção`}</StatusPill>
          )}
        </>
      }
    >
      <Box>
        {pendencies.length === 0 && (
          <Box
            display="flex"
            alignItems="center"
            gap={2}
            py={3}
            borderTop={`1px solid ${OVERVIEW_COLORS.line2}`}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "50%",
                bgcolor: "#E6F4EA",
                color: "#1B5E20",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <MdCheck size={22} aria-hidden />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 15, fontWeight: 500 }}>
                Nenhuma pendência
              </Typography>
              <Typography sx={{ fontSize: 14, color: OVERVIEW_COLORS.muted }}>
                Tudo em dia nesta etapa.
              </Typography>
            </Box>
          </Box>
        )}

        {pendencies.map((pendency) => (
          <PendencyRow
            key={pendency.key}
            pendency={pendency}
            action={
              pendency.key === "budget-approval" ? (
                <Button
                  variant="contained"
                  disableElevation
                  onClick={onGoToBudget}
                  sx={buttonSx}
                >
                  Ir para Orçamento
                </Button>
              ) : (
                <Button
                  variant="outlined"
                  onClick={onConfirmEventData}
                  disabled={confirming}
                  startIcon={
                    confirming ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : (
                      <MdCheck />
                    )
                  }
                  sx={buttonSx}
                >
                  Confirmar dados
                </Button>
              )
            }
          />
        ))}
      </Box>
    </OverviewCard>
  );
}

function FinancialSummaryCard({ onSeeAll }: { onSeeAll: () => void }) {
  const { project, summary, event } = useProject();
  const projectStatus = project?.status?.status;

  const rows = buildFinancialStageRows({
    projectStatus,
    summary,
    customerBudget: event?.customerBudget,
  });
  const audit = getAuditResults(projectStatus, summary?.AUDITED);

  const auditItems = [
    {
      label: "Prejuízo",
      value: audit.loss !== null ? formatMoney(audit.loss) : null,
    },
    {
      label: "Rentabilidade",
      value:
        audit.profitability !== null
          ? formatPercent(audit.profitability)
          : null,
    },
  ];

  return (
    <OverviewCard
      title="Resumo financeiro"
      testId="overview-financial"
      action={<CardLink onClick={onSeeAll}>Ver completo</CardLink>}
    >
      <Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: FINANCIAL_GRID_COLUMNS,
            gap: 1.5,
            alignItems: "center",
            px: 1.25,
            py: 0.75,
            minHeight: 36,
            lineHeight: 1.25,
            bgcolor: OVERVIEW_COLORS.headBg,
            borderRadius: "6px",
            fontSize: 13,
            fontWeight: 500,
            color: OVERVIEW_COLORS.muted,
          }}
        >
          <span>Etapa</span>
          <span style={{ textAlign: "right" }}>Valor total do evento</span>
          <span style={{ textAlign: "right" }}>Total de receitas</span>
        </Box>

        {rows.map((row) => (
          <Box
            key={row.key}
            data-testid={`financial-stage-${row.key}`}
            data-reached={row.reached ? "true" : "false"}
            sx={{
              display: "grid",
              gridTemplateColumns: FINANCIAL_GRID_COLUMNS,
              gap: 1.5,
              alignItems: "center",
              px: 1.25,
              minHeight: 44,
              borderBottom: `1px solid ${OVERVIEW_COLORS.line2}`,
              borderRadius: "6px",
              bgcolor: row.isCurrent ? OVERVIEW_COLORS.blueSoft : "transparent",
              color: row.reached ? OVERVIEW_COLORS.ink : OVERVIEW_COLORS.muted2,
              fontSize: 14,
            }}
          >
            <Box
              display="flex"
              alignItems="center"
              gap={1.25}
              fontWeight={row.isCurrent ? 500 : 400}
              minWidth={0}
            >
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "3px",
                  flexShrink: 0,
                  bgcolor: row.reached ? row.color : "#DFE4EA",
                }}
              />
              <span>{row.label}</span>
              {row.isCurrent && (
                <StatusPill tone="blue" small>
                  Atual
                </StatusPill>
              )}
            </Box>
            <Box sx={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
              {row.value === null ? "—" : formatMoney(row.value)}
            </Box>
            <Box
              data-testid={`financial-revenue-${row.key}`}
              sx={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}
            >
              {row.revenue === null ? "—" : formatMoney(row.revenue)}
            </Box>
          </Box>
        ))}
      </Box>

      <Box display="flex" flexDirection="column" gap={1}>
        {auditItems.map((item) => (
          <Box
            key={item.label}
            data-testid={`audit-${item.label}`}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              px: 1.75,
              py: 1.25,
              borderRadius: "8px",
              border: `1px solid ${
                audit.available ? "#C3CBD6" : OVERVIEW_COLORS.line
              }`,
              bgcolor: audit.available ? OVERVIEW_COLORS.headBg : "#fff",
            }}
          >
            <Box>
              <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                {item.label}
              </Typography>
              <Typography sx={{ fontSize: 12, color: OVERVIEW_COLORS.muted }}>
                {audit.available
                  ? "Apurado na auditoria"
                  : "Disponível após a auditoria"}
              </Typography>
            </Box>
            <Typography
              sx={{
                ml: "auto",
                fontSize: 18,
                fontWeight: 500,
                color: audit.available
                  ? OVERVIEW_COLORS.ink
                  : OVERVIEW_COLORS.muted2,
              }}
            >
              {item.value ?? "—"}
            </Typography>
          </Box>
        ))}
      </Box>
    </OverviewCard>
  );
}

function BudgetsCard({ onSeeAll }: { onSeeAll: () => void }) {
  const { project } = useProject();
  const { budgetList } = useBudget();
  const projectStatus = project?.status?.status;
  const rows = budgetList ?? [];

  const headSx = {
    fontSize: 13,
    color: OVERVIEW_COLORS.muted,
    fontWeight: 500,
    bgcolor: OVERVIEW_COLORS.headBg,
    borderBottom: 0,
    py: 1.25,
    whiteSpace: "nowrap" as const,
  };
  const cellSx = {
    borderBottom: `1px solid ${OVERVIEW_COLORS.line2}`,
    py: 1.5,
    fontSize: 14,
  };

  return (
    <OverviewCard
      title="Orçamentos do projeto"
      testId="overview-budgets"
      action={<CardLink onClick={onSeeAll}>Ver na aba Orçamento</CardLink>}
    >
      {rows.length === 0 ? (
        <Typography sx={{ fontSize: 14, color: OVERVIEW_COLORS.muted }}>
          Nenhum orçamento criado neste projeto.
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small" aria-label="Orçamentos do projeto">
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...headSx, borderRadius: "6px 0 0 6px" }}>
                  #
                </TableCell>
                <TableCell sx={headSx}>Nome</TableCell>
                <TableCell sx={headSx}>Tipo</TableCell>
                <TableCell sx={headSx}>Status</TableCell>
                <TableCell sx={headSx} align="right">
                  Valor
                </TableCell>
                <TableCell sx={{ ...headSx, borderRadius: "0 6px 6px 0" }}>
                  Atualizado em
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, position) => {
                const status = getBudgetStatusDisplay(row, projectStatus);
                const updatedAt = row.updatedAt ? new Date(row.updatedAt) : null;
                return (
                  <TableRow key={row._id ?? position}>
                    <TableCell sx={{ ...cellSx, color: OVERVIEW_COLORS.muted }}>
                      {getBudgetNumber(position)}
                    </TableCell>
                    <TableCell sx={{ ...cellSx, fontWeight: 500 }}>
                      {getBudgetName(row, position)}
                    </TableCell>
                    <TableCell sx={cellSx}>
                      {(row.type && BUDGET_TYPE_LABEL[row.type]) || "—"}
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <StatusPill tone={status.tone}>{status.label}</StatusPill>
                    </TableCell>
                    <TableCell
                      sx={{ ...cellSx, fontVariantNumeric: "tabular-nums" }}
                      align="right"
                    >
                      {formatMoney(getBudgetRowValue(row))}
                    </TableCell>
                    <TableCell sx={{ ...cellSx, color: OVERVIEW_COLORS.muted }}>
                      {updatedAt && !Number.isNaN(updatedAt.getTime())
                        ? format(updatedAt, "dd/MM/yyyy")
                        : "—"}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </OverviewCard>
  );
}

const ProjectOverview = ({ onNavigateTab }: ProjectOverviewProps) => {
  const {
    project,
    event,
    loadingProject,
    setTabActive,
    goToRegistrationStep,
    confirmEventData,
  } = useProject();
  const { budgetList } = useBudget();
  const [confirming, setConfirming] = useState(false);
  const queryClient = useQueryClient();

  const pendencies = useMemo(
    () =>
      buildPendencies({
        projectStatus: project?.status?.status,
        eventStartDate: event?.startDate,
        eventDataConfirmedAt: project?.eventDataConfirmedAt,
        budgets: budgetList ?? [],
      }),
    [
      project?.status?.status,
      project?.eventDataConfirmedAt,
      event?.startDate,
      budgetList,
    ]
  );

  const navigate = (tab: TabActive) =>
    onNavigateTab ? onNavigateTab(tab) : setTabActive(tab);

  async function handleConfirmEventData() {
    setConfirming(true);
    try {
      await confirmEventData();
      toast.success("Dados do evento confirmados");
      // A confirmação grava o último editor; atualiza "Última edição".
      void queryClient.invalidateQueries(
        queryKeys.projectLastEditorAudit(project!._id)
      );
    } catch {
      toast.error("Não foi possível confirmar os dados do evento.");
    } finally {
      setConfirming(false);
    }
  }

  if (loadingProject || !project) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" gap={2} py={10}>
        <Typography variant="h6">Carregando...</Typography>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      data-testid="project-overview"
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        p: { xs: 2, md: 3 },
        maxWidth: 1480,
      }}
    >
      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "repeat(2, minmax(0, 1fr))" },
        }}
      >
        <EventCard onEdit={() => goToRegistrationStep(RegistrationStep.EVENT)} />
        <ClientCard
          onEdit={() => goToRegistrationStep(RegistrationStep.CLIENT)}
        />
      </Box>

      <TeamCard onEdit={() => goToRegistrationStep(RegistrationStep.TEAM)} />

      <Box
        sx={{
          display: "grid",
          gap: 2,
          alignItems: "start",
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            lg: "minmax(0, 2fr) minmax(0, 1fr)",
          },
        }}
      >
        <PendenciesCard
          pendencies={pendencies}
          onGoToBudget={() => navigate(TabActive.BUDGET)}
          onConfirmEventData={handleConfirmEventData}
          confirming={confirming}
        />
        <FinancialSummaryCard onSeeAll={() => navigate(TabActive.SUMMARY)} />
      </Box>

      <BudgetsCard onSeeAll={() => navigate(TabActive.BUDGET)} />
    </Box>
  );
};

export default ProjectOverview;
